import { Content, ContentNode, ContentNodeType } from '@amzn/awscat-pattern-library-service-model';
import { listEpics } from '../services/EpicOperations';
import { SpecialSectionId } from '../services/SectionOperations';
import { listTroubleshootings } from '../services/TroubleshootingOperations';

/** Identifies a particular richtext (Tiptap) document within a Content object. */
export type RichtextDocId =
  | {
      /** The Tiptap document is a page, a sub-page, or a section of a page */
      readonly type: 'section';
      /** The id of a node inside the Content object (should have type of "doc"). */
      readonly sectionId: string;
      readonly nodeType: ContentNodeType;
    }
  | {
      /** The tiptap document is an epic's title */
      readonly type: 'epic_title';
      readonly epicId: string;
    }
  | {
      /** The tiptap document is an epic's description */
      readonly type: 'epic_description';
      readonly epicId: string;
    }
  | {
      /** The tiptap document is an epic task's title */
      readonly type: 'epic_task_title';
      readonly taskId: string;
    }
  | {
      /** The tiptap document is an epic task's description */
      readonly type: 'epic_task_description';
      readonly taskId: string;
    }
  | {
      /** The tiptap document is a troubleshooting issue */
      readonly type: 'troubleshooting_issue';
      readonly troubleshootingId: string;
    }
  | {
      /** The tiptap document is a troubleshooting solution */
      readonly type: 'troubleshooting_solution';
      readonly troubleshootingId: string;
    };

/** Produces a stable string key from a RichtextDocId for use in maps and comparisons. */
export function stringifyDocId(id: RichtextDocId): string {
  switch (id.type) {
    case 'section':
      return `section:${id.nodeType}:${id.sectionId}`;
    case 'epic_title':
      return `epic:${id.epicId}:title`;
    case 'epic_description':
      return `epic:${id.epicId}:description`;
    case 'epic_task_title':
      return `task:${id.taskId}:title`;
    case 'epic_task_description':
      return `task:${id.taskId}:description`;
    case 'troubleshooting_issue':
      return `troubleshooting:${id.troubleshootingId}:issue`;
    case 'troubleshooting_solution':
      return `troubleshooting:${id.troubleshootingId}:solution`;
  }
}

/** Collects RichtextDocIds in document order for navigation ordering. */
export function collectDocIds(content: Content): readonly RichtextDocId[] {
  const ids: RichtextDocId[] = [];

  function walkNode(node: ContentNode): void {
    ids.push({ type: 'section', sectionId: node.id, nodeType: node.nodeType });

    if (node.contentSections) {
      for (const section of node.contentSections) {
        if (section.id === SpecialSectionId.Epics) {
          ids.push({
            type: 'section',
            sectionId: SpecialSectionId.Epics,
            nodeType: ContentNodeType.Section,
          });
          walkEpics(content);
          continue;
        }
        if (section.id === SpecialSectionId.Troubleshooting) {
          ids.push({
            type: 'section',
            sectionId: SpecialSectionId.Troubleshooting,
            nodeType: ContentNodeType.Section,
          });
          walkTroubleshootings(content);
          continue;
        }
        ids.push({ type: 'section', sectionId: section.id, nodeType: ContentNodeType.Section });
      }
    }

    if (node.subContent) {
      for (const child of node.subContent) {
        walkNode(child);
      }
    }
  }

  function walkEpics(c: Content): void {
    const epics = listEpics(c);
    for (const epic of epics) {
      ids.push({ type: 'epic_title', epicId: epic.id });
      ids.push({ type: 'epic_description', epicId: epic.id });
      for (const task of epic.tasks) {
        ids.push({ type: 'epic_task_title', taskId: task.id });
        ids.push({ type: 'epic_task_description', taskId: task.id });
      }
    }
  }

  function walkTroubleshootings(c: Content): void {
    const troubleshootings = listTroubleshootings(c);
    for (const item of troubleshootings) {
      ids.push({ type: 'troubleshooting_issue', troubleshootingId: item.id });
      ids.push({ type: 'troubleshooting_solution', troubleshootingId: item.id });
    }
  }

  walkNode(content.contentData);
  return ids;
}

export function docIdEquals(a: RichtextDocId, b: RichtextDocId): boolean {
  switch (a.type) {
    case 'section':
      return b.type === 'section' && a.sectionId === b.sectionId && a.nodeType === b.nodeType;
    case 'epic_title':
      return b.type === 'epic_title' && b.epicId === a.epicId;
    case 'epic_description':
      return b.type === 'epic_description' && b.epicId === a.epicId;
    case 'epic_task_title':
      return b.type === 'epic_task_title' && b.taskId === a.taskId;
    case 'epic_task_description':
      return b.type === 'epic_task_description' && b.taskId === a.taskId;
    case 'troubleshooting_issue':
      return b.type === 'troubleshooting_issue' && b.troubleshootingId === a.troubleshootingId;
    case 'troubleshooting_solution':
      return b.type === 'troubleshooting_solution' && b.troubleshootingId === a.troubleshootingId;
  }
}
